"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { GitMerge, ClipboardText, UsersThree, ShieldCheck, type IconProps } from "@phosphor-icons/react";

import { Link } from "@/navigation";

const FEATURE_KEYS = [
	{ icon: GitMerge, titleKey: "whyChooseFeature1Title", descKey: "whyChooseFeature1Desc" },
	{ icon: ClipboardText, titleKey: "whyChooseFeature2Title", descKey: "whyChooseFeature2Desc" },
	{ icon: UsersThree, titleKey: "whyChooseFeature3Title", descKey: "whyChooseFeature3Desc" },
	{ icon: ShieldCheck, titleKey: "whyChooseFeature4Title", descKey: "whyChooseFeature4Desc" },
] as const;

const iconSize = 22;
const iconProps: IconProps = { size: iconSize, weight: "fill", className: "text-themePrimary400" };

const CTA_IMAGES = [
	"/image/new-version/casting/Die-Casting/Why-Choose/metal-casting-1.webp",
	"/image/new-version/casting/Die-Casting/Why-Choose/metal-casting-2.webp",
];

export default function DieCastingWhyChoose() {
	const t = useTranslations("new-die-casting");

	return (
		<section className="w-full bg-white">
			<div className="container py-8 sm:py-10 md:py-12 lg:py-16">
				<h2 className="mb-5 text-center text-2xl font-bold leading-tight text-black sm:mb-6 sm:text-[30px] md:mb-8 md:text-4xl lg:mb-10">
					{t("whyChooseTitle")}
				</h2>

				{/* 上 3 / 左 1 + 右 CTA（两列），显式占位与早期压铸版一致 */}
				<div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-stretch md:gap-5">
					{FEATURE_KEYS.map((item, index) => {
						const Icon = item.icon;
						return (
							<div
								key={item.titleKey}
								className={`flex min-h-0 flex-col border border-[#e5e7eb] bg-white p-4 text-left sm:p-5 ${
									index < 3 ? "md:row-start-1" : ""
								} ${index === 3 ? "md:col-start-1 md:row-start-2" : ""}`}
							>
								<div className="mb-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-themePrimary400 bg-[#f0f9eb]">
									<Icon {...iconProps} />
								</div>
								<h3 className="mb-1.5 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(item.titleKey)}
								</h3>
								<p className="grow text-sm leading-relaxed text-gray-600 md:text-base">{t(item.descKey)}</p>
							</div>
						);
					})}

					<div
						className="relative h-full min-h-[200px] overflow-hidden bg-[#4a4a4a] px-4 py-7 sm:py-8 md:col-span-2 md:col-start-2 md:row-start-2 md:min-h-0"
						style={{ backgroundColor: "#4a4a4a" }}
					>
						<div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-7 px-3 sm:gap-12 sm:px-6">
							<div className="relative h-32 w-[9.5rem] shrink-0 opacity-50 sm:h-[9.5rem] sm:w-[14rem] md:h-40 md:w-[15.5rem]">
								<Image src={CTA_IMAGES[0]} alt="" fill className="object-cover object-center rounded-sm" sizes="256px" />
							</div>
							<div className="relative h-[7.25rem] w-[6.25rem] shrink-0 opacity-50 sm:h-[8.5rem] sm:w-28 md:h-36 md:w-32">
								<Image src={CTA_IMAGES[1]} alt="" fill className="object-cover object-center rounded-sm" sizes="200px" />
							</div>
						</div>
						<div className="pointer-events-none absolute inset-0 bg-black/25" />
						<div className="relative z-10 flex h-full min-h-[168px] flex-col items-center justify-center text-center md:min-h-0">
							<h3 className="mb-1.5 text-lg font-bold text-white sm:text-xl md:text-2xl">
								{t("whyChooseCtaTitle")}
							</h3>
							<p className="mb-4 max-w-md text-xs text-white/90 sm:text-sm">{t("whyChooseCtaSubtitle")}</p>
							<Link
								href="/upload"
								className="rounded-lg bg-themePrimary400 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#76b82e] hover:text-white sm:px-7 sm:text-base"
							>
								{t("whyChooseCtaButton")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
